/**
 * Plus-value immobilière des particuliers : abattements pour durée de détention.
 *
 * Barème en vigueur, reproduit de la feuille `Plus value particuliers` :
 *
 *  - **Impôt sur le revenu** : 6 % par année de détention de la 6ᵉ à la 21ᵉ (96 %), puis 4 %
 *    la 22ᵉ → exonération totale à **22 ans**.
 *  - **Prélèvements sociaux** : 1,65 % par année de la 6ᵉ à la 21ᵉ (26,4 %), 1,6 % la 22ᵉ,
 *    puis 9 % par année de la 23ᵉ à la 30ᵉ → exonération totale à **30 ans**.
 *
 * Les pourcentages sont manipulés en **centièmes de point** (entiers) et convertis une seule
 * fois. Sinon `1 - 0,55` donne `0,44999999999999996` et le taux à 25 ans n'est plus 3,375 %
 * exact, mais 3,3749999999999995 %.
 */
/** Taux d'impôt sur le revenu applicable à la plus-value des particuliers. */
export const DEFAULT_CAPITAL_GAINS_INCOME_TAX_RATE = 0.19;
const BASIS = 10_000;
const FIRST_ALLOWANCE_YEAR = 5; // aucun abattement jusqu'à la 5ᵉ année incluse
/** Abattement de l'impôt sur le revenu, en centièmes de point. */
function incomeTaxAllowanceBp(yearsHeld) {
    const years = Math.floor(yearsHeld);
    if (years <= FIRST_ALLOWANCE_YEAR)
        return 0;
    if (years <= 21)
        return (years - FIRST_ALLOWANCE_YEAR) * 600;
    return BASIS; // 22ᵉ année : 96 % + 4 %
}
/** Abattement des prélèvements sociaux, en centièmes de point. */
function socialAllowanceBp(yearsHeld) {
    const years = Math.floor(yearsHeld);
    if (years <= FIRST_ALLOWANCE_YEAR)
        return 0;
    if (years <= 21)
        return (years - FIRST_ALLOWANCE_YEAR) * 165;
    const afterYear22 = (21 - FIRST_ALLOWANCE_YEAR) * 165 + 160; // 2 640 + 160 = 28 %
    if (years <= 30)
        return afterYear22 + (years - 22) * 900;
    return BASIS;
}
/** Taux d'abattement cumulé de l'impôt sur le revenu, entre 0 et 1. */
export function incomeTaxAllowance(yearsHeld) {
    return incomeTaxAllowanceBp(yearsHeld) / BASIS;
}
/** Taux d'abattement cumulé des prélèvements sociaux, entre 0 et 1. */
export function socialAllowance(yearsHeld) {
    return socialAllowanceBp(yearsHeld) / BASIS;
}
/**
 * Surtaxe sur les plus-values immobilières imposables de plus de 50 000 € (art. 1609 nonies G
 * du CGI, défaut P6 : le classeur l'ignore). `taxableGain` est la plus-value **après**
 * abattement pour durée de détention de l'impôt sur le revenu.
 *
 * Chaque tranche de 10 000 € ou 40 000 € applique un taux croissant de 2 à 6 %, avec un
 * lissage linéaire à l'entrée de la tranche pour éviter l'effet de seuil.
 */
const SURCHARGE_BANDS = [
    { floor: 50_000, ceiling: 60_000, rate: 0.02, smoothing: 1 / 20 },
    { floor: 100_000, ceiling: 110_000, rate: 0.03, smoothing: 1 / 10 },
    { floor: 150_000, ceiling: 160_000, rate: 0.04, smoothing: 1 / 15 },
    { floor: 200_000, ceiling: 210_000, rate: 0.05, smoothing: 1 / 20 },
    { floor: 250_000, ceiling: 260_000, rate: 0.06, smoothing: 1 / 25 },
];
export function capitalGainsSurcharge(taxableGain) {
    if (!(taxableGain > 50_000))
        return 0;
    // Dernière tranche atteinte : son taux, lissé si l'on est encore dans sa fenêtre d'entrée.
    let surcharge = 0;
    for (const band of SURCHARGE_BANDS) {
        if (taxableGain <= band.floor)
            break;
        surcharge =
            taxableGain <= band.ceiling
                ? band.rate * taxableGain - (band.ceiling - taxableGain) * band.smoothing
                : band.rate * taxableGain;
    }
    return surcharge;
}
/**
 * Taux effectif d'imposition d'une plus-value après abattements.
 *
 * Exemple du classeur, à 20 ans : `19 % × (1 − 90 %) + 17,2 % × (1 − 24,75 %) = 14,843 %`.
 * Exemple du foyer, à 25 ans, non-résident EEE : IR exonéré, et `7,5 % × 45 % = 3,375 %`.
 */
export function capitalGainsTaxRate(params) {
    const incomeBp = incomeTaxAllowanceBp(params.yearsHeld);
    const socialBp = socialAllowanceBp(params.yearsHeld);
    const incomeTaxRate = params.incomeTaxRate * ((BASIS - incomeBp) / BASIS);
    const socialRate = params.socialRate * ((BASIS - socialBp) / BASIS);
    return {
        incomeTaxAllowance: incomeBp / BASIS,
        socialAllowance: socialBp / BASIS,
        incomeTaxRate,
        socialRate,
        total: incomeTaxRate + socialRate,
    };
}
//# sourceMappingURL=capital-gains.js.map